import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useSettingsQuery, useUpdateSettingsMutation } from '@/api/endpoints';
import { Button } from '@/design/Button';
import { ErrorState } from '@/design/EmptyState';
import { Checkbox, Field, FieldRow, TextInput } from '@/design/Field';
import { Panel, PanelBody } from '@/design/Panel';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';

/**
 * The business's own details.
 *
 * A page rather than a modal: eight fields, and three of them are printed on a
 * document a customer keeps. This is a form somebody sits down with once and
 * then does not touch for a year, which is exactly the wrong shape for a box
 * that closes if you tap beside it.
 *
 * Every field says what it affects rather than only what it is called. "PAN
 * number" is a label; "printed on every invoice" is the reason somebody should
 * get it right.
 */
export function SettingsPage(): ReactElement {
  const { data, isLoading, isError, refetch } = useSettingsQuery();
  const [save, saving] = useUpdateSettingsMutation();
  const toast = useToast();

  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [stampUrl, setStampUrl] = useState('');
  const [pan, setPan] = useState('');
  const [vatRegistered, setVatRegistered] = useState(false);
  const [prefix, setPrefix] = useState('');
  const [turnaround, setTurnaround] = useState('120');

  useEffect(() => {
    if (data === undefined) return;
    setName(data.name);
    setCity(data.city);
    setAddress(data.address ?? '');
    setStampUrl(data.stamp_url ?? '');
    setPan(data.pan_number ?? '');
    setVatRegistered(data.vat_registered ?? false);
    setPrefix(data.booking_reference_prefix ?? 'BK');
    setTurnaround(String(data.turnaround_buffer_minutes ?? 120));
  }, [data]);

  const problems = readApiError(saving.error);

  async function submit(): Promise<void> {
    try {
      await save({
        name,
        city,
        address,
        stamp_url: stampUrl,
        pan_number: pan,
        vat_registered: vatRegistered,
        booking_reference_prefix: prefix,
        turnaround_buffer_minutes: Number(turnaround),
      }).unwrap();
      toast.done('Settings saved.');
    } catch {
      // Field errors render beside their fields; the summary is below.
    }
  }

  if (isError) {
    return (
      <ErrorState
        headline="Settings did not load"
        detail="The server did not answer. Check the connection and load them again."
        onRetry={() => void refetch()}
        retryLabel="Load again"
      />
    );
  }

  if (isLoading || data === undefined) {
    return <div className="h-64" aria-busy="true" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-display font-semibold">Settings</h1>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        <Panel
          title="The business"
          note="What appears in the header of this app and at the top of every invoice"
          current
        >
          <PanelBody>
            <div className="flex flex-col gap-4">
              <FieldRow>
                <Field label="Business name" required error={problems.fields['name']}>
                  {({ id, describedBy }) => (
                    <TextInput id={id} describedBy={describedBy} value={name} onChange={setName} />
                  )}
                </Field>
                <Field label="City" required error={problems.fields['city']}>
                  {({ id, describedBy }) => (
                    <TextInput id={id} describedBy={describedBy} value={city} onChange={setCity} />
                  )}
                </Field>
              </FieldRow>

              {/* A path into the back office's own assets, or a full URL.
                  There is no upload here: this product has no media storage,
                  and adding a volume, a serving route and an upload endpoint
                  to print one picture is a lot of machinery for one picture.
                  The preview is what confirms the value is right. */}
              <Field label="Stamp image" error={problems.fields['stamp_url']}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    value={stampUrl}
                    onChange={setStampUrl}
                  />
                )}
              </Field>

              {stampUrl !== '' && (
                <div className="flex items-center gap-3">
                  <img
                    src={stampUrl}
                    alt=""
                    className="h-20 w-20 object-contain"
                  />
                  <span className="text-small text-ink-muted">
                    This is what prints on an invoice. Nothing here means no stamp.
                  </span>
                </div>
              )}

              <Field
                label="Address"
                error={problems.fields['address']}
              >
                {({ id, describedBy }) => (
                  <textarea
                    id={id}
                    aria-describedby={describedBy}
                    rows={3}
                    value={address}
                    onChange={(event) => setAddress(event.target.value)}
                    className="w-full rounded-sm border border-rule-strong bg-panel px-2.5 py-2 text-body"
                  />
                )}
              </Field>
            </div>
          </PanelBody>
        </Panel>

        <Panel title="Tax" note="Decides what every invoice says, so it is worth being sure">
          <PanelBody>
            <div className="flex flex-col gap-4">
              <Checkbox
                label="Registered for VAT (invoices show the 13% inside each total)"
                checked={vatRegistered}
                onChange={setVatRegistered}
              />

              <Field
                label="PAN number"
                error={problems.fields['pan_number']}
              >
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    identifier
                    value={pan}
                    onChange={setPan}
                  />
                )}
              </Field>

              {/* The one thing on this screen somebody could get badly wrong
                  without noticing, so it is said in words next to the field. */}
              <p className="border-l-[3px] border-l-signal bg-signal-wash px-3 py-2 text-small">
                Rates include VAT. A Rs 6,500 day rate is what the customer pays, and the invoice
                shows Rs 748.67 of VAT inside it — not added on top.
              </p>
            </div>
          </PanelBody>
        </Panel>

        <Panel title="How the counter behaves" note="Changing these does not affect anything already taken">
          <PanelBody>
            <div className="flex flex-col gap-4">
              <FieldRow>
                <Field
                  label="Booking reference prefix, as in BK-83-0001"
                  error={problems.fields['booking_reference_prefix']}
                >
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      describedBy={describedBy}
                      identifier
                      value={prefix}
                      onChange={(value) => setPrefix(value.toUpperCase())}
                    />
                  )}
                </Field>

                <Field
                  label="Turnaround gap"
                  error={problems.fields['turnaround_buffer_minutes']}
                >
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      describedBy={describedBy}
                      value={turnaround}
                      onChange={setTurnaround}
                    />
                  )}
                </Field>
              </FieldRow>

              <p className="text-small text-ink-muted">
                Bookings already taken keep the prefix and the gap they were given. The turnaround
                is stored on each booking when it is made, so changing it here cannot make a
                booking that was legal yesterday overlap today.
              </p>
            </div>
          </PanelBody>
        </Panel>

        {problems.detail !== undefined && (
          <p
            className="border border-alert/40 border-l-[3px] border-l-alert bg-alert-wash px-3 py-2 font-medium text-alert"
            role="alert"
          >
            {problems.detail}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            intent="primary"
            size="touch"
            type="submit"
            disabled={saving.isLoading || name.trim() === '' || city.trim() === ''}
            title={
              name.trim() === '' || city.trim() === ''
                ? 'The business name and city are both needed.'
                : undefined
            }
          >
            {saving.isLoading ? 'Saving' : 'Save settings'}
          </Button>
          <span className="text-small text-ink-muted">
            Support handle: <span className="identifier">{data.slug}</span>
          </span>
        </div>
      </form>
    </div>
  );
}
